import { Box, Divider } from "@mui/material"
import React, { useEffect } from "react"
import useCountdown from "src/hooks/use-countdown"
import useLiveConcertState from "src/hooks/use-live-concert-state"
import useTranslate, { Namespaces } from "src/hooks/use-translate"

import { TranslationKey } from "../../types"
import { Duration, secondsToDuration } from "../../utilities/common"
import { s } from "../../utilities/common"
import { theme, TI } from "../../utilities/theme"
import { DGText } from "../common/DGText"
import { LiveConcertExtract } from "../Sliders/Slider"

export const LiveConcertCountdown = ({
  liveConcert,
  resetFn,
}: LiveConcertCountdownProps & { resetFn?: () => void }) => {
  const { status, startTime, streamStartTime } =
    useLiveConcertState(liveConcert)
  const showCountdown = status === "upcoming"
  const liveConcertStartTime =
    streamStartTime ||
    startTime ||
    liveConcert.streamStartTime ||
    liveConcert.startTime

  return showCountdown ? (
    <Countdown startTime={liveConcertStartTime} resetFn={resetFn} />
  ) : null
}

type LiveConcertCountdownProps = {
  liveConcert: Pick<
    LiveConcertExtract,
    "id" | "startTime" | "endTime" | "streamStartTime" | "reruns"
  >
}

const durationUnitLabels: Record<
  keyof Duration,
  TranslationKey<Namespaces.Default>
> = {
  days: "component__countdown_days_unit",
  hours: "component__countdown_hours_unit",
  minutes: "component__countdown_minutes_unit",
  seconds: "component__countdown_seconds_unit",
}

/**
 * Renders a countdown component based on a startTime
 */
export const Countdown = ({
  startTime,
  resetFn,
}: {
  startTime: string
  resetFn?: () => void
}) => {
  const t = useTranslate()
  const secondsUntilStart = useCountdown(startTime)
  const duration = secondsToDuration(secondsUntilStart)
  const units = [
    { value: duration.days, label: t(durationUnitLabels.days) },
    { value: duration.hours, label: t(durationUnitLabels.hours) },
    { value: duration.minutes, label: t(durationUnitLabels.minutes) },
    { value: duration.seconds, label: t(durationUnitLabels.seconds) },
  ]

  // if the countdown ends, do a reset if a reset function is provided
  useEffect(() => {
    const sub = resetFn
      ? setTimeout(() => {
          resetFn()
        }, secondsUntilStart * 1000)
      : undefined
    return () => {
      if (sub) clearTimeout(sub)
    }
  }, [])

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        height: s("62px"),
        backgroundColor: "rgba(10, 15, 23, 0.7)",
        alignItems: "center",
        pl: s("32px"),
        pr: s("32px"),
        borderRadius: s("8px"),
        "& > :not(:last-child)": {
          mr: s("16px"),
        },
      }}
    >
      {/* “On Air In” */}
      <DGText
        tx={TI.Caption03tv}
        sx={{
          color: theme.palette.white.main,
        }}
      >
        {t("component__countdown_title")}
      </DGText>
      {units.map((unit, index) => (
        <Box
          key={"bannercountdown" + index}
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {/* divider */}
          {index > 0 && (
            <Divider
              orientation={"vertical"}
              sx={{
                borderColor: theme.palette.white[20],
                height: s("32px"),
                mr: s("16px"),
              }}
            />
          )}
          {/* unit */}
          <DGText
            tx={TI.Subheadline03tv}
            sx={{
              color: theme.palette.tertiary.accentYellow,
              minWidth: s("44px"),
              textAlign: "center",
              height: s("55px"),
            }}
          >
            {unit.value}
          </DGText>
          {/* value */}
          <DGText tx={TI.Caption03tv} sx={{ color: theme.palette.white.main }}>
            {unit.label}
          </DGText>
        </Box>
      ))}
    </Box>
  )
}
